import {ReactElement, useEffect} from "react";
import {
    BackgroundJob,
    JobStatus, JobType,
    useGetJobLazyQuery,
    useGetLatestJobLazyQuery
} from "../../../__generated__/generated_types";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../constants/Colors";
import {formatterDateTimeUS} from "../../../utils/formatters";
import {Check, ErrorOutline} from "@material-ui/icons";

export interface RunningJobRendererProps {
    jobId?: string;
    jobDone?: (job: BackgroundJob) => void;
}

function renderJobStatus(copyJob: BackgroundJob): ReactElement {
    if(copyJob.status === JobStatus.Submitted) {
        return <><Inline color={COLORS.PRIMARY_500} /> Job Submitted {formatterDateTimeUS(copyJob.createdAt)}</>;
    } else if(copyJob.status === JobStatus.Running) {
        return <><Inline color={COLORS.PRIMARY_500} /> Job Started at {formatterDateTimeUS(copyJob.started)}</>;
    } else if(copyJob.status === JobStatus.Completed) {
        return <><Check htmlColor={COLORS.GREEN_500} /> Job Completed at {formatterDateTimeUS(copyJob.ended)}</>;
    } else {
        return <><ErrorOutline htmlColor={COLORS.NEGATIVE_COLOR} /> Job Failed at {formatterDateTimeUS(copyJob.ended)}</>;
    }
}

export function RunningJobRenderer(props: RunningJobRendererProps): ReactElement {
    const [getJob, {data: jobData, loading: loadingJob}] = useGetJobLazyQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    function fetchLatestJob() {
        if(!props.jobId) {
            return;
        }

        getJob({
            variables: {
                jobId: props.jobId
            }
        });
    }

    useEffect(() => {
        fetchLatestJob();
    }, [props.jobId]);

    useEffect(() => {
        if(!jobData?.getJob || loadingJob) {
            return;
        }

        const job = jobData.getJob;
        if(job.status === JobStatus.Completed || job.status === JobStatus.Failed) {
            if(props.jobDone) {
                props.jobDone(job);
            }
            return;
        }

        const interval = setInterval(() => {
            fetchLatestJob();
        }, 25000);
        return () => clearInterval(interval);
    }, [jobData, loadingJob]);

    if(!jobData?.getJob) {
        return <></>;
    }

    return <span>
        {renderJobStatus(jobData.getJob)}
    </span>;
}

interface RunningJobRendererLatestJobProps extends RunningJobRendererProps {
    jobType: JobType;
}

export function RunningJobRendererLatestJob(props: RunningJobRendererLatestJobProps): ReactElement {
    const [getLatestJob, {data: latestJob}] = useGetLatestJobLazyQuery({
        fetchPolicy: "no-cache",
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        if(props.jobId === undefined) {
            getLatestJob({
                variables: {
                    jobType: props.jobType
                }
            });
        }
    }, []);

    if(props.jobId) {
        return <RunningJobRenderer jobId={props.jobId} jobDone={props.jobDone} />;
    }
    return <RunningJobRenderer jobId={latestJob?.getLatestJob?.id} jobDone={props.jobDone} />;
}